import api from './api';

const BASE = '/hr/email-campaigns';

/**
 * @typedef {'all'|'by_role'|'by_department'|'by_employee'|'advanced'} EmailCampaignAudienceMode
 * @typedef {'draft'|'scheduled'|'sending'|'sent'|'cancelled'|'failed'} EmailCampaignStatus
 * @typedef {'now'|'once'|'recurring'} EmailCampaignSendMode
 * @typedef {'daily'|'weekly'|'monthly'} EmailCampaignFrequency
 */

/**
 * @typedef {Object} EmailCampaignAudienceRules
 * @property {'and'|'or'} [logic]
 * @property {string[]} [roles]
 * @property {number[]} [department_ids]
 * @property {number[]} [employee_ids]
 * @property {string[]} [statuses]
 * @property {boolean} [active_only]
 */

/**
 * @typedef {Object} EmailCampaignRecurrence
 * @property {EmailCampaignFrequency} frequency
 * @property {number} interval
 * @property {number[]} [weekdays]
 * @property {'day_of_month'|'nth_weekday'} [mode]
 * @property {number} [day]
 * @property {number} [weekday]
 * @property {number} [nth]
 * @property {{type:'never'|'end_date'|'after_occurrences', end_date?:string, occurrences?:number}} [end]
 */

/**
 * @typedef {Object} EmailCampaignPayload
 * @property {string} [title]
 * @property {EmailCampaignAudienceMode} [audience_mode]
 * @property {EmailCampaignAudienceRules} [audience_rules]
 * @property {string} [subject]
 * @property {string} [preheader]
 * @property {string} [html_body]
 * @property {string} [html_body_en]
 * @property {string} [html_body_ar]
 * @property {string} [text_body]
 * @property {string} [text_body_en]
 * @property {string} [text_body_ar]
 * @property {boolean} [is_bilingual]
 * @property {string} [from_name]
 * @property {string} [from_email]
 * @property {string} [reply_to]
 * @property {boolean} [send_in_app_notification]
 * @property {string} [timezone]
 */

/**
 * @typedef {Object} EmailCampaignSchedulePayload
 * @property {EmailCampaignSendMode} send_mode
 * @property {string} timezone
 * @property {string} [send_at]
 * @property {EmailCampaignRecurrence} [recurrence]
 */

const unwrap = (response) => response?.data;

const firstError = (errors) => {
  const out = {};
  Object.entries(errors || {}).forEach(([field, messages]) => {
    out[field] = Array.isArray(messages) ? String(messages[0] || '') : String(messages || '');
  });
  return out;
};

export const mapLaravelError = (error) => {
  const payload = error?.response?.data || {};

  if (payload?.errors && typeof payload.errors === 'object') {
    return {
      message: payload.message || 'Validation failed.',
      fieldErrors: firstError(payload.errors),
      status: error?.response?.status || 422,
    };
  }

  return {
    message: payload?.message || error?.message || 'Request failed.',
    fieldErrors: {},
    status: error?.response?.status || 500,
  };
};

export const listEmailCampaigns = async (params = {}) => unwrap(await api.get(BASE, { params }));

export const createEmailCampaign = async (payload) => unwrap(await api.post(BASE, payload));

export const getEmailCampaign = async (id) => unwrap(await api.get(`${BASE}/${id}`));

export const updateEmailCampaign = async (id, payload) => unwrap(await api.put(`${BASE}/${id}`, payload));

export const estimateEmailCampaignRecipients = async (id, payload = {}) =>
  unwrap(await api.post(`${BASE}/${id}/estimate`, payload));

export const getEmailCampaignLookups = async () => unwrap(await api.get(`${BASE}/lookups`));

export const searchEmailCampaignEmployees = async (params = {}) =>
  unwrap(await api.get(`${BASE}/employees/search`, { params }));

export const sendEmailCampaignTest = async (id, payload) =>
  unwrap(await api.post(`${BASE}/${id}/send-test`, payload));

export const scheduleEmailCampaign = async (id, payload) =>
  unwrap(await api.post(`${BASE}/${id}/schedule`, payload));

export const cancelEmailCampaign = async (id) => unwrap(await api.post(`${BASE}/${id}/cancel`));

export const deleteEmailCampaign = async (id) => unwrap(await api.delete(`${BASE}/${id}`));

export const duplicateEmailCampaign = async (id) => unwrap(await api.post(`${BASE}/${id}/duplicate`));

export const listEmailCampaignDeliveries = async (id, params = {}) =>
  unwrap(await api.get(`${BASE}/${id}/deliveries`, { params }));

export const uploadEmailCampaignAttachments = async (id, files = [], options = {}) => {
  const form = new FormData();
  files.forEach((file) => form.append('attachments[]', file));

  return unwrap(
    await api.post(`${BASE}/${id}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: options.onUploadProgress,
    })
  );
};

export const deleteEmailCampaignAttachment = async (id, attachmentId) =>
  unwrap(await api.delete(`${BASE}/${id}/attachments/${attachmentId}`));

export const downloadEmailCampaignAttachment = async (id, attachmentId) => {
  const response = await api.get(`${BASE}/${id}/attachments/${attachmentId}/download`, {
    responseType: 'blob',
  });

  const header = String(response?.headers?.['content-disposition'] || '');
  const match = header.match(/filename="?([^"]+)"?/i);

  return {
    blob: response.data,
    filename: match?.[1] || `attachment-${attachmentId}`,
  };
};

const emailCampaignsApi = {
  listEmailCampaigns,
  createEmailCampaign,
  getEmailCampaign,
  updateEmailCampaign,
  estimateEmailCampaignRecipients,
  getEmailCampaignLookups,
  searchEmailCampaignEmployees,
  sendEmailCampaignTest,
  scheduleEmailCampaign,
  cancelEmailCampaign,
  deleteEmailCampaign,
  duplicateEmailCampaign,
  listEmailCampaignDeliveries,
  uploadEmailCampaignAttachments,
  deleteEmailCampaignAttachment,
  downloadEmailCampaignAttachment,
  mapLaravelError,
};

export default emailCampaignsApi;
